/**
 * ReferralDashboard
 *
 * Main component for the "Refer & Earn" referral program dashboard.
 * Displays referral code, how it works, referrals table, and FAQ.
 */

import { useEffect, useState } from 'react';
import { ContentCopy, ExpandMore } from '@mui/icons-material';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useEventTracking } from '@services/statsig';

import { useReferralsQuery } from '../store/api';
import { useCopyReferralLink } from '../store/hooks';
import type { ReferralEntry } from '../types';
import { formatRewardAmount, getReferralRewardText } from '../utils';

/**
 * Format currency amount
 */
const formatCurrency = (amount: number): string => {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount);
};

/**
 * Get status chip color based on referral status
 */
const getStatusColor = (
  status: string,
): 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' => {
  const statusLower = status.toLowerCase();
  if (statusLower === 'subscribed') return 'primary';
  if (statusLower === 'trialing') return 'info';
  if (statusLower === 'canceled' || statusLower === 'cancelled') return 'error';
  if (statusLower === 'paused') return 'warning';
  return 'default';
};

/**
 * ReferralDashboard component
 */
export const ReferralDashboard = () => {
  const { data, isLoading, error } = useReferralsQuery();
  const { copyLink } = useCopyReferralLink();
  const { referral } = useEventTracking();
  const [copiedCode, setCopiedCode] = useState<string | null>(null);

  const referralCode = data?.referral_code;
  const referrals = data?.referrals || [];
  const rewardText = getReferralRewardText(data?.referrer_reward_amount, data?.signup_discount_amount);

  // Track page view on mount
  useEffect(() => {
    if (!isLoading && !error) {
      referral.pageViewed({
        has_referral_code: !!referralCode,
        referrals_count: referrals.length,
        total_earnings: data?.referral_earnings,
      });
    }
  }, [isLoading, error, referralCode, referrals.length, data?.referral_earnings, referral]);

  const handleCopyLink = async (code: string) => {
    const success = await copyLink(code, 'referral_dashboard');
    if (success) {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(null), 2000);
    }
  };

  const handleFaqExpand = (index: number, question: string) => {
    referral.faqExpanded({
      question_index: index,
      question_text: question,
    });
  };

  if (isLoading) {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
          <CircularProgress size={32} />
        </Box>
      </Card>
    );
  }

  if (error) {
    return (
      <Card variant="outlined" sx={{ p: 3, mb: 3 }}>
        <Alert severity="error">Unable to load referral information. Please try again later.</Alert>
      </Card>
    );
  }

  return (
    <Card variant="outlined" sx={{ p: 3 }}>
      <Typography variant="h6" gutterBottom>
        Refer & Earn
      </Typography>
      <Divider sx={{ mb: 2 }} />

      {/* Subtitle */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h5" component="p" sx={{ fontWeight: 600, mb: 0.5 }}>
          {rewardText.symmetric ? (
            <>
              You both earn{' '}
              <Box component="span" sx={{ color: 'primary.main' }}>
                {formatRewardAmount(rewardText.referrerAmount)}
              </Box>
            </>
          ) : (
            <>
              You earn{' '}
              <Box component="span" sx={{ color: 'primary.main' }}>
                {formatRewardAmount(rewardText.referrerAmount)}
              </Box>
              , they get{' '}
              <Box component="span" sx={{ color: 'primary.main' }}>
                {formatRewardAmount(rewardText.signupAmount)}
              </Box>{' '}
              off
            </>
          )}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {rewardText.description}
        </Typography>
      </Box>

      {/* Referral Code Section */}
      {referralCode && (
        <Box sx={{ mb: 4 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 2,
              p: 2,
              bgcolor: 'grey.50',
              borderRadius: 1,
              border: 1,
              borderColor: 'grey.200',
            }}
          >
            <Typography
              variant="h6"
              sx={{
                fontFamily: 'monospace',
                fontWeight: 600,
                color: 'primary.main',
                flex: 1,
              }}
            >
              {referralCode}
            </Typography>
            <Button
              variant="contained"
              size="small"
              onClick={() => handleCopyLink(referralCode)}
              startIcon={<ContentCopy />}
              sx={{ flexShrink: 0 }}
            >
              {copiedCode === referralCode ? 'Copied!' : 'Copy link'}
            </Button>
          </Box>
        </Box>
      )}

      {/* How It Works Section */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          How it works
        </Typography>
        <Grid container spacing={2}>
          {[
            {
              number: 1,
              title: 'Share your referral link',
              description: 'Send your link or code to a colleague.',
            },
            {
              number: 2,
              title: 'They start a free trial',
              description:
                'They use your link (code auto-applied) or enter your code during sign-up.',
            },
            {
              number: 3,
              title: rewardText.headline,
              description: rewardText.symmetric
                ? `Once they subscribe, you both get ${formatRewardAmount(rewardText.referrerAmount)} credit.`
                : `Once they subscribe, you get ${formatRewardAmount(rewardText.referrerAmount)} credit and they get ${formatRewardAmount(rewardText.signupAmount)} off.`,
            },
          ].map(step => (
            <Grid size={{ xs: 12, md: 4 }} key={step.number}>
              <Card
                variant="outlined"
                sx={{
                  p: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1.5,
                }}
              >
                <Box
                  sx={{
                    width: 32,
                    height: 32,
                    borderRadius: '50%',
                    bgcolor: 'primary.main',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 600,
                    fontSize: '1rem',
                  }}
                >
                  {step.number}
                </Box>
                <Box>
                  <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                    {step.number === 3 ? (
                      rewardText.symmetric ? (
                        <>
                          You both earn{' '}
                          <Box component="span" sx={{ color: 'primary.main' }}>
                            {formatRewardAmount(rewardText.referrerAmount)}
                          </Box>
                        </>
                      ) : (
                        <>
                          You earn{' '}
                          <Box component="span" sx={{ color: 'primary.main' }}>
                            {formatRewardAmount(rewardText.referrerAmount)}
                          </Box>
                          , they get{' '}
                          <Box component="span" sx={{ color: 'primary.main' }}>
                            {formatRewardAmount(rewardText.signupAmount)}
                          </Box>{' '}
                          off
                        </>
                      )
                    ) : (
                      step.title
                    )}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {step.description}
                  </Typography>
                </Box>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* Your Referrals Table Section */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          Your referrals
        </Typography>
        {referrals.length > 0 ? (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
                <TableCell sx={{ fontWeight: 600 }} align="right">
                  Credit
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {referrals.map((referral: ReferralEntry, index: number) => (
                <TableRow key={index}>
                  <TableCell>{referral.name || '—'}</TableCell>
                  <TableCell>
                    {referral.status === 'Invited' ? (
                      <Typography variant="body2">{referral.status}</Typography>
                    ) : (
                      <Chip
                        label={referral.status}
                        size="small"
                        color={getStatusColor(referral.status)}
                        variant={
                          referral.status.toLowerCase() === 'subscribed' ? 'filled' : 'outlined'
                        }
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {formatCurrency(referral.credit)}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        ) : (
          <Typography variant="body2" color="text.secondary">
            No referrals yet. Share your referral code to get started!
          </Typography>
        )}
      </Box>

      <Divider sx={{ my: 3 }} />

      {/* FAQ Section */}
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2 }}>
          FAQ
        </Typography>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          {[
            {
              question: 'When is credit applied?',
              answer: 'After the referred user subscribes to JotPsych.',
            },
            {
              question: 'Can I refer multiple people?',
              answer: 'Yes — there are no limits',
            },
            {
              question: 'Where do they enter the code?',
              answer: 'During sign-up for their free trial.',
            },
          ].map((faq, index) => (
            <Accordion
              key={index}
              variant="outlined"
              disableGutters
              onChange={(_, expanded) => {
                if (expanded) {
                  handleFaqExpand(index, faq.question);
                }
              }}
            >
              <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {faq.question}
                </Typography>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" color="text.secondary">
                  {faq.answer}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      </Box>
    </Card>
  );
};
